import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { TotalEmissionsReportSettings } from '@/hooks'
import { Drawer, WidgetSpinner } from '@/components'
import { ResetReportSettingsButton } from '@/components/molecules/ResetReportSettingsButton/ResetReportSettingsButton'
import { DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS } from '@/utils/constants'
import useSustainabilityStore from '../../stores/useSustainabilityStore/useSustainabilityStore'
import { Scope2TypeSelect } from './Scope2TypeSelect'

export type ElectricityMixUsageDrawerProps = {
    reportSettings: TotalEmissionsReportSettings
    setReportSettings: (settings: Partial<TotalEmissionsReportSettings>) => void
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    toggleButtonRef?: React.Ref<HTMLButtonElement>
    showResetReportSettingsButton?: boolean
    isMarketBasedScope2Available?: boolean
    isDataReady?: boolean
}

export const ElectricityMixUsageDrawer: React.FC<
    ElectricityMixUsageDrawerProps
> = (props) => {
    const {
        reportSettings,
        setReportSettings,
        isOpen,
        setIsOpen,
        toggleButtonRef,
        showResetReportSettingsButton,
        isDataReady,
        isMarketBasedScope2Available = true,
    } = props

    const { t } = useTranslation('translations')

    const hasDefaultSettings = useSustainabilityStore(
        (state) => state.electricityMix.hasDefaultSettings
    )
    const isResetHidden =
        showResetReportSettingsButton !== undefined
            ? !showResetReportSettingsButton
            : hasDefaultSettings

    return (
        <Drawer
            drawerToggleLabel={t('sustainability.report_settings.title')}
            drawerToggleIconName="tune"
            title={t('sustainability.report_settings.title')}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            toggleButtonRef={toggleButtonRef}
            headerContent={
                <ResetReportSettingsButton
                    onClick={() => {
                        setReportSettings(
                            DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS
                        )
                    }}
                    isHidden={isResetHidden}
                    data-testid={'reset-settings-button'}
                />
            }
        >
            {isDataReady ? (
                <div className="space-y-3">
                    <div className="text-heading-caps">
                        {t(
                            'sustainability.report_settings.operational_emissions'
                        )}
                    </div>
                    {reportSettings.scope2type && (
                        <Scope2TypeSelect
                            isMarketBasedScope2Available={
                                isMarketBasedScope2Available
                            }
                            value={reportSettings.scope2type}
                            onChange={(value) => {
                                setReportSettings({
                                    ...reportSettings,
                                    scope2type: value,
                                })
                            }}
                        />
                    )}
                </div>
            ) : (
                <div className="flex h-full items-center justify-center">
                    <WidgetSpinner />
                </div>
            )}
        </Drawer>
    )
}
